/**
 * Shopping List → RECIPES tab: the week's make-list, grouped by day, as a
 * look-ahead calendar for batch prep. Rows come from `buildRecipeDayGroups`
 * (recipe-pack inclusion + merge rules) and click through to the shared
 * recipe detail slot.
 *
 * Forecast rows mirror the ingredient list's treatment exactly: the main
 * quantity slot stays BLANK, the estimate renders as a muted italic `~N`
 * (or "No history yet"), and a Forecast badge marks the row — estimates
 * are never dressed up as placed orders.
 */

import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { ListGroupHeader } from '@/components/chefs/list-group-header'
import { RecipeSwapMenu } from '@/components/chefs/recipe-swap-menu'
import { formatQuantity } from '@/lib/chefs/format'
import { groupByTypeAndTemp, PACK_TYPE_LABELS } from '@/lib/chefs/pack-groups'
import { formatLongDate } from '@/lib/chefs/print-plan'
import type { RecipeDayEntry, RecipeDayGroup } from '@/lib/chefs/recipes-by-day'
import { MEALTIME_LABEL } from '@/lib/chefs/types'

export function ShoppingRecipesTab({
  dayGroups,
  ordersIncoming,
  activeComponentId,
  onSelect,
  emptyFallback,
}: {
  dayGroups: RecipeDayGroup[]
  /** The range's order window is still open — empty days read as pending. */
  ordersIncoming: boolean
  activeComponentId: string | null
  onSelect: (componentId: string, variantId?: string | null) => void
  /** Rendered instead of day sections when the whole range has nothing. */
  emptyFallback: ReactNode
}) {
  const totalEntries = dayGroups.reduce((sum, g) => sum + g.entries.length, 0)
  if (totalEntries === 0) {
    return <>{emptyFallback}</>
  }
  return (
    <div className="flex flex-col gap-4 px-2 pb-4 lg:px-0">
      {dayGroups.map((group) => (
        <section key={group.iso} className="overflow-hidden rounded-2xl border border-border bg-background shadow-xs">
          <div className="flex items-baseline justify-between gap-3 border-b border-border px-4 py-3 lg:px-6">
            <h2 className="text-base font-medium text-foreground">{formatLongDate(group.date)}</h2>
            <span className="text-sm text-muted-foreground">
              {group.entries.length} recipe{group.entries.length === 1 ? '' : 's'}
            </span>
          </div>
          {group.entries.length === 0 ? (
            <p className="px-4 py-4 text-sm italic text-muted-foreground lg:px-6">
              {ordersIncoming
                ? 'Ordering still open — recipes appear as orders are placed.'
                : 'Nothing to make this day.'}
            </p>
          ) : (
            // Default grouping: TYPE (Mains / Sides / Condiments / Beverages);
            // within a type the rows are simply ORDERED hot → chilled →
            // ambient (no temperature headings — the order is the
            // organization; rows with no temp on file trail).
            groupByTypeAndTemp(
              group.entries,
              (entry) => entry.typeGroup,
              (entry) => entry.temperature,
            ).map((typeGroup) => (
              <div key={typeGroup.group}>
                <ListGroupHeader label={PACK_TYPE_LABELS[typeGroup.group]} count={typeGroup.items.length} />
                <div className="flex flex-col divide-y divide-border">
                  {typeGroup.items.map((entry) => (
                    <RecipeRow
                      key={entry.key}
                      entry={entry}
                      selected={entry.componentId === activeComponentId}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      ))}
    </div>
  )
}

function RecipeRow({
  entry,
  selected,
  onSelect,
}: {
  entry: RecipeDayEntry
  selected: boolean
  onSelect: (componentId: string, variantId?: string | null) => void
}) {
  // Same row anatomy as the kitchen /orders page's DeliverableRow: a narrow
  // muted quantity column, name + inline badges on one vertically-centered
  // line, sublabels (the swap chip) beneath, chevron affordance on the right.
  return (
    <div
      className={cn(
        'group flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/60',
        selected && 'bg-muted',
      )}
      onClick={() => onSelect(entry.componentId, entry.componentVariantId)}>
      <span className="flex w-12 shrink-0 flex-col text-xs font-medium text-muted-foreground">
        {entry.source === 'orders' ? (
          <span>{formatQuantity(entry.totalQty, entry.unit)}</span>
        ) : entry.estimatedServings !== null ? (
          <span
            className="italic"
            title={
              entry.forecastMissingHistory
                ? 'Partial estimate — some schools have no ordering history yet, so the real need may be higher.'
                : undefined
            }>
            ~{formatQuantity(Math.round(entry.estimatedServings), 'x')}
          </span>
        ) : (
          <span className="text-[10px] italic leading-tight">No history yet</span>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-foreground">{entry.name}</span>
          {entry.servingSize ? (
            <span className="shrink-0 text-xs text-muted-foreground">{entry.servingSize} per serving</span>
          ) : null}
          {entry.mealtimes.map((m) => (
            <Badge key={m} variant="secondary" className="shrink-0 px-2">
              {MEALTIME_LABEL[m]}
            </Badge>
          ))}
          {entry.source === 'forecast' ? (
            <Badge variant="warning" className="shrink-0 px-2">
              Forecast
            </Badge>
          ) : null}
        </span>
        {entry.source === 'orders' ? (
          // PROD-225: swap this day's component for a pre-approved alternate,
          // order-local, across every shipment the row aggregates. Renders
          // ONLY when an alternate exists — same chip as the ingredient-level
          // brand dropdown. Clicks here must not open the detail panel.
          <span onClick={(e) => e.stopPropagation()}>
            <RecipeSwapMenu
              shipmentIds={entry.shipmentIds}
              componentId={entry.componentId}
              componentName={entry.name}
            />
          </span>
        ) : null}
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
        strokeWidth={1.5}
      />
    </div>
  )
}
